import { useCallback, useEffect, useState } from 'react';
import { useStore } from '../state/store';
import {
  adminListUsers,
  adminUpdateUser,
  adminDeleteUser,
  adminListOrgs,
  adminGetOrg,
  adminCreateOrg,
  adminUpdateOrg,
  adminDeleteOrg,
  adminCreateAccount,
  adminGetSettings,
  adminSetSignup,
  orgCreateUser,
  orgUpdateUser,
  orgRename,
  CloudError,
  type AdminUserRow,
  type AdminOrgRow,
} from '../lib/cloud';
import { IconPlus, IconTrash } from './icons';

function fmtSize(bytes: number): string {
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

type Tab = 'users' | 'orgs';

export function AdminView() {
  const setView = useStore((s) => s.setView);
  const me = useStore((s) => s.auth.user);
  const [tab, setTab] = useState<Tab>('users');

  return (
    <div className="admin-shell">
      <div className="admin-head">
        <span className="admin-title">SPblock admin console</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            className={`btn ${tab === 'users' ? 'btn-primary' : ''}`}
            onClick={() => setTab('users')}
          >
            Accounts
          </button>
          <button
            className={`btn ${tab === 'orgs' ? 'btn-primary' : ''}`}
            onClick={() => setTab('orgs')}
          >
            Organizations
          </button>
          <button className="btn" onClick={() => setView('home')}>
            Back to studio
          </button>
        </div>
      </div>

      <div className="admin-body">
        {tab === 'users' ? <UsersTab me={me?.id ?? null} /> : <OrgsTab />}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Accounts                                                            */
/* ------------------------------------------------------------------ */

function UsersTab({ me }: { me: string | null }) {
  const pushLine = useStore((s) => s.pushLine);
  const [users, setUsers] = useState<AdminUserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [signupEnabled, setSignupEnabled] = useState(false);
  const [acctEmail, setAcctEmail] = useState('');
  const [acctPassword, setAcctPassword] = useState('');
  const [creatingAcct, setCreatingAcct] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const [usersData, settingsData] = await Promise.all([adminListUsers(), adminGetSettings()]);
      setUsers(usersData.users);
      setSignupEnabled(settingsData.signupEnabled);
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Could not load accounts.');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function toggleDisabled(row: AdminUserRow): Promise<void> {
    setError(null);
    try {
      await adminUpdateUser(row.id, { disabled: !row.disabled });
      pushLine('info', `${row.name} is now ${row.disabled ? 'active' : 'disabled'}.`);
      void refresh();
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Update failed.');
    }
  }

  async function deleteUser(row: AdminUserRow): Promise<void> {
    setError(null);
    try {
      await adminDeleteUser(row.id);
      pushLine('info', `Deleted ${row.name} and their projects.`);
      setConfirmDelete(null);
      void refresh();
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Delete failed.');
    }
  }

  function createAccount(): void {
    setError(null);
    setCreatingAcct(true);
    adminCreateAccount(acctEmail.trim(), acctPassword)
      .then(({ user }) => {
        pushLine('ok', `Account created for ${user.email}.`);
        setAcctEmail('');
        setAcctPassword('');
        void refresh();
      })
      .catch((err) => setError(err instanceof CloudError ? err.message : 'Could not create the account.'))
      .finally(() => setCreatingAcct(false));
  }

  return (
    <div className="admin-card">
      <div className="admin-card-head">
        <span className="admin-card-title">All accounts</span>
        <button className="btn btn-sm" onClick={() => void refresh()}>
          Refresh
        </button>
      </div>

      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--c-border)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={signupEnabled}
            onChange={() => {
              const next = !signupEnabled;
              setSignupEnabled(next);
              void adminSetSignup(next).then(() => {
                pushLine('info', next
                  ? 'Self-serve registration is now available on the login page.'
                  : 'Self-serve registration is now hidden and blocked.');
              });
            }}
          />
          Allow self-serve registration on the login page ("Create account" tab)
        </label>

        <div className="field-row" style={{ marginTop: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="field" style={{ flex: '1 1 200px', marginBottom: 0 }}>
            <div className="field-label"><span>Create an account for a user</span></div>
            <input
              type="email"
              placeholder="user@example.com"
              value={acctEmail}
              onChange={(e) => setAcctEmail(e.target.value)}
            />
          </div>
          <div className="field" style={{ flex: '1 1 180px', marginBottom: 0 }}>
            <div className="field-label">
              <span>Password</span>
            </div>
            <input
              type="password"
              value={acctPassword}
              onChange={(e) => setAcctPassword(e.target.value)}
            />
          </div>
          <button
            className="btn btn-primary"
            style={{ height: 38 }}
            disabled={creatingAcct || !acctEmail.trim() || acctPassword.length < 4}
            onClick={createAccount}
          >
            <IconPlus width={14} height={14} />
            {creatingAcct ? 'Creating…' : 'Create account'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 18px 0' }}>
          <div className="form-error err">{error}</div>
        </div>
      )}

      {users === null && !error && <div className="admin-empty">Loading…</div>}
      {users !== null && users.length === 0 && !error && (
        <div className="admin-empty">No accounts yet.</div>
      )}

      {users !== null && users.length > 0 && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Role</th>
              <th>Organization</th>
              <th>Projects</th>
              <th>Storage used</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td style={{ fontWeight: 600 }}>
                  {u.name}
                  {me === u.id && <span className="badge info" style={{ marginLeft: 8 }}>you</span>}
                </td>
                <td>
                  {u.role === 'main_admin' && <span className="badge info">SPadministrator</span>}
                  {u.role === 'org_admin' && <span className="badge ok">Administrator (org)</span>}
                  {u.role === 'org_manager' && <span className="badge ok">Manager (org)</span>}
                  {u.role === 'member' && <span className="mono">Member (org)</span>}
                </td>
                <td>{u.orgName ? <span className="mono">{u.orgName}</span> : <span className="mono">—</span>}</td>
                <td className="mono">{u.projectCount}</td>
                <td className="mono">{fmtSize(u.usedBytes)}</td>
                <td>
                  {u.disabled
                    ? <span className="badge err">disabled</span>
                    : <span className="badge ok">active</span>}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn btn-sm" onClick={() => void toggleDisabled(u)}>
                    {u.disabled ? 'Enable' : 'Disable'}
                  </button>{' '}
                  {confirmDelete === u.id ? (
                    <button className="btn btn-sm btn-danger" onClick={() => void deleteUser(u)}>
                      Confirm delete
                    </button>
                  ) : (
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => setConfirmDelete(u.id)}
                      disabled={me === u.id}
                      title={me === u.id ? 'You cannot delete your own account' : 'Delete account and their projects'}
                    >
                      <IconTrash width={13} height={13} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ padding: '10px 18px 14px' }}>
        <p className="field-note" style={{ marginBottom: 0 }}>
          Disabled accounts cannot sign in. Storage limits are configured per organization in
          the Organizations tab; personal accounts created here get a 2 MB quota.
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Organizations — drill-down: org list → org detail → members         */
/* ------------------------------------------------------------------ */

function OrgsTab() {
  const pushLine = useStore((s) => s.pushLine);
  const [orgs, setOrgs] = useState<AdminOrgRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', quotaMb: '10', adminUsername: '', adminPassword: '' });

  const refresh = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const data = await adminListOrgs();
      setOrgs(data.orgs);
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Could not load organizations.');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function createOrg(): Promise<void> {
    setError(null);
    const quotaMb = Number(form.quotaMb);
    if (!form.name.trim()) return setError('Enter an organization name.');
    if (!Number.isInteger(quotaMb) || quotaMb < 1) return setError('Storage quota must be a positive integer (MB).');
    if (!form.adminUsername.trim()) return setError('Enter an administrator username for the organization.');
    if (form.adminPassword.length < 4) return setError('The organization admin password must be at least 4 characters.');

    setCreating(true);
    try {
      await adminCreateOrg({
        name: form.name.trim(),
        quotaMb,
        adminUsername: form.adminUsername.trim(),
        adminPassword: form.adminPassword,
      });
      pushLine('ok', `Organization "${form.name.trim()}" created.`);
      setForm({ name: '', quotaMb: '10', adminUsername: '', adminPassword: '' });
      void refresh();
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Could not create the organization.');
    } finally {
      setCreating(false);
    }
  }

  const selectedName = orgs?.find((o) => o.id === selectedOrgId)?.name ?? '';

  if (selectedOrgId !== null) {
    return (
      <OrgDetail
        orgId={selectedOrgId}
        orgName={selectedName}
        onBack={() => { setSelectedOrgId(null); void refresh(); }}
        onNotify={(level, message) => pushLine(level, message)}
        onError={(message) => setError(message)}
      />
    );
  }

  return (
    <>
      <div className="admin-card" style={{ marginBottom: 20 }}>
        <div className="admin-card-head">
          <span className="admin-card-title">Create organization</span>
        </div>
        <div style={{ padding: '14px 18px' }}>
          <div className="field-row" style={{ flexWrap: 'wrap' }}>
            <div className="field" style={{ flex: '2 1 200px', marginBottom: 0 }}>
              <div className="field-label"><span>Organization name</span></div>
              <input
                type="text"
                value={form.name}
                placeholder="e.g. Springfield High"
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="field" style={{ flex: '1 1 110px', marginBottom: 0 }}>
              <div className="field-label">
                <span>Storage (MB)</span>
              </div>
              <input
                type="number"
                min={1}
                max={10240}
                value={form.quotaMb}
                onChange={(e) => setForm((f) => ({ ...f, quotaMb: e.target.value }))}
              />
            </div>
          </div>
          <div className="field-row" style={{ marginTop: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="field" style={{ flex: '1 1 180px', marginBottom: 0 }}>
              <div className="field-label"><span>Org admin username</span></div>
              <input
                type="text"
                value={form.adminUsername}
                placeholder="letters, digits, . _ -"
                spellCheck={false}
                onChange={(e) => setForm((f) => ({ ...f, adminUsername: e.target.value }))}
              />
            </div>
            <div className="field" style={{ flex: '1 1 180px', marginBottom: 0 }}>
              <div className="field-label">
                <span>Org admin password</span>
              </div>
              <input
                type="password"
                value={form.adminPassword}
                onChange={(e) => setForm((f) => ({ ...f, adminPassword: e.target.value }))}
              />
            </div>
            <button
              className="btn btn-primary"
              style={{ height: 38 }}
              onClick={() => void createOrg()}
              disabled={creating || !form.name.trim()}
            >
              <IconPlus width={14} height={14} />
              {creating ? 'Creating…' : 'Create organization'}
            </button>
          </div>
          <p className="field-note" style={{ marginBottom: 0 }}>
            The org admin signs in through "Login with an organization" with the organization
            name plus this username and password, and creates members inside these limits.
          </p>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-head">
          <span className="admin-card-title">Organizations</span>
          <button className="btn btn-sm" onClick={() => void refresh()}>
            Refresh
          </button>
        </div>

        {error && (
          <div style={{ padding: '12px 18px 0' }}>
            <div className="form-error err">{error}</div>
          </div>
        )}

        {orgs === null && !error && <div className="admin-empty">Loading…</div>}
        {orgs !== null && orgs.length === 0 && !error && (
          <div className="admin-empty">No organizations yet — create the first one above.</div>
        )}

        {orgs !== null && orgs.length > 0 && (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Users</th>
                <th>Storage used</th>
                <th>Storage limit</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((o) => (
                <tr key={o.id}>
                  <td>
                    <button
                      className="btn btn-sm"
                      style={{ fontWeight: 700, color: 'var(--c-primary-dark)', paddingLeft: 0 }}
                      onClick={() => setSelectedOrgId(o.id)}
                      title="Open this organization"
                    >
                      {o.name}
                    </button>
                  </td>
                  <td className="mono">{o.userCount} members</td>
                  <td className="mono">{fmtSize(o.usedBytes)}</td>
                  <td className="mono">{o.quotaMb} MB</td>
                  <td>
                    {o.disabled
                      ? <span className="badge err">disabled</span>
                      : <span className="badge ok">active</span>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn btn-sm" onClick={() => setSelectedOrgId(o.id)}>
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Organization detail (breadcrumb: Organizations / name / members)    */
/* ------------------------------------------------------------------ */

interface OrgDetailData {
  organization: {
    id: string;
    name: string;
    quotaMb: number;
    maxUsers: number;
    disabled: boolean;
    createdAt: string;
  };
  members: {
    id: string;
    username: string;
    role: string;
    disabled: boolean;
    createdAt: string;
    projectCount: number;
    usedBytes: number;
  }[];
}

function OrgDetail(props: {
  orgId: string;
  orgName: string;
  onBack: () => void;
  onNotify: (level: 'info' | 'ok', message: string) => void;
  onError: (message: string) => void;
}) {
  const me = useStore((s) => s.auth.user);
  const [data, setData] = useState<OrgDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [quotaDraft, setQuotaDraft] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [renameText, setRenameText] = useState(props.orgName);
  const [renaming, setRenaming] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState<'org_admin' | 'org_manager' | 'member'>('member');
  const [creatingUser, setCreatingUser] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const detail = await adminGetOrg(props.orgId);
      setData(detail);
      setQuotaDraft(String(detail.organization.quotaMb));
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Could not load the organization.');
    }
  }, [props.orgId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function saveLimits(): Promise<void> {
    const quotaMb = Number(quotaDraft);
    if (!Number.isInteger(quotaMb) || quotaMb < 1 || quotaMb > 10240) {
      setError('Storage quota must be between 1 and 10240 MB.');
      return;
    }
    setError(null);
    try {
      await adminUpdateOrg(props.orgId, { quotaMb, maxUsers: 0 });
      props.onNotify('ok', `Storage limit for "${props.orgName}" updated to ${quotaMb} MB.`);
      void refresh();
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Update failed.');
    }
  }

  async function toggleDisabled(): Promise<void> {
    if (!data) return;
    setError(null);
    try {
      await adminUpdateOrg(props.orgId, { disabled: !data.organization.disabled });
      props.onNotify('info', `"${props.orgName}" is now ${data.organization.disabled ? 'active' : 'disabled'}.`);
      void refresh();
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Update failed.');
    }
  }

  async function saveRename(): Promise<void> {
    setError(null);
    setRenaming(true);
    try {
      await orgRename(renameText.trim(), props.orgId);
      props.onNotify('ok', `The organization was renamed to "${renameText.trim()}".`);
      void refresh();
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Rename failed.');
    } finally {
      setRenaming(false);
    }
  }

  async function createOrgUser(): Promise<void> {
    setError(null);
    setCreatingUser(true);
    try {
      const { user } = await orgCreateUser(newUsername.trim(), newPassword, newRole, props.orgId);
      props.onNotify('ok', `Created ${user.role} "${user.username}".`);
      setNewUsername('');
      setNewPassword('');
      setNewRole('member');
      void refresh();
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Could not create the user.');
    } finally {
      setCreatingUser(false);
    }
  }

  async function changeMemberRole(member: { id: string; username: string }, role: string): Promise<void> {
    setError(null);
    try {
      await orgUpdateUser(member.id, { role: role as 'org_admin' | 'org_manager' | 'member' }, props.orgId);
      props.onNotify('ok', `"${member.username}" is now ${role}.`);
      void refresh();
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Could not change the role.');
      void refresh();
    }
  }

  async function deleteOrg(): Promise<void> {
    setError(null);
    try {
      await adminDeleteOrg(props.orgId);
      props.onNotify('info', `Deleted "${props.orgName}" with all of its accounts and projects.`);
      props.onBack();
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Delete failed.');
    }
  }

  async function toggleMember(member: OrgDetailData['members'][number]): Promise<void> {
    setError(null);
    try {
      await adminUpdateUser(member.id, { disabled: !member.disabled });
      props.onNotify('info', `${member.username} is now ${member.disabled ? 'active' : 'disabled'}.`);
      void refresh();
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Update failed.');
    }
  }

  async function deleteMember(member: OrgDetailData['members'][number]): Promise<void> {
    setError(null);
    try {
      await adminDeleteUser(member.id);
      props.onNotify('info', `Deleted ${member.username} and their projects.`);
      setConfirmDelete(null);
      void refresh();
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Delete failed.');
    }
  }

  const usedBytes = (data?.members ?? []).reduce((sum, m) => sum + m.usedBytes, 0);
  const dirty =
    !!data && (Number(quotaDraft) !== data.organization.quotaMb);

  return (
    <div className="admin-card">
      <div className="admin-card-head" style={{ flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn btn-sm" onClick={props.onBack} title="Back to all organizations">
            Organizations
          </button>
          <span className="mono">/</span>
          <span className="admin-card-title" style={{ fontSize: 14 }}>{props.orgName}</span>
          {data?.organization.disabled && <span className="badge err">disabled</span>}
        </span>
        <button className="btn btn-sm" onClick={() => void refresh()}>
          Refresh
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px 18px 0' }}>
          <div className="form-error err">{error}</div>
        </div>
      )}

      {data && (
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--c-border)' }}>
          <div className="field-row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="field" style={{ flex: '1 1 130px', marginBottom: 0 }}>
              <div className="field-label"><span>Storage limit (MB)</span></div>
              <input
                className="quota-input"
                type="number"
                min={1}
                max={10240}
                value={quotaDraft}
                onChange={(e) => setQuotaDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void saveLimits(); }}
              />
            </div>
            {dirty && (
              <button className="btn btn-primary" style={{ height: 38 }} onClick={() => void saveLimits()}>
                Save limits
              </button>
            )}
            <div className="field" style={{ flex: '1 1 160px', marginBottom: 0 }}>
              <div className="field-label"><span>Organization name</span></div>
              <input
                type="text"
                value={renameText}
                spellCheck={false}
                onChange={(e) => setRenameText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && renameText.trim() && renameText.trim() !== props.orgName) void saveRename(); }}
              />
            </div>
            {renameText.trim() && renameText.trim() !== props.orgName && (
              <button className="btn btn-primary" style={{ height: 38 }} onClick={() => void saveRename()} disabled={renaming}>
                {renaming ? 'Saving…' : 'Rename organization'}
              </button>
            )}
            <button className="btn" style={{ height: 38 }} onClick={() => void toggleDisabled()}>
              {data.organization.disabled ? 'Enable organization' : 'Disable organization'}
            </button>
            {confirmDelete === '__org__' ? (
              <button className="btn btn-sm btn-danger" onClick={() => void deleteOrg()}>
                Confirm delete
              </button>
            ) : (
              <button className="btn btn-sm btn-danger" onClick={() => setConfirmDelete('__org__')}>
                Delete organization
              </button>
            )}
          </div>
          <p className="field-note" style={{ marginBottom: 0 }}>
            {data.members.length} members · {fmtSize(usedBytes)} of{' '}
            {data.organization.quotaMb} MB used · created {fmtDate(data.organization.createdAt)}
          </p>
        </div>
      )}

      {data && (
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--c-border)' }}>
          <div className="field-row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="field" style={{ flex: '1 1 160px', marginBottom: 0 }}>
              <div className="field-label"><span>Username</span></div>
              <input
                type="text"
                value={newUsername}
                placeholder="letters, digits, . _ -"
                spellCheck={false}
                onChange={(e) => setNewUsername(e.target.value)}
              />
            </div>
            <div className="field" style={{ flex: '1 1 140px', marginBottom: 0 }}>
              <div className="field-label"><span>Password</span></div>
              <input
                type="text"
                value={newPassword}
                placeholder="(choose at first sign-in)"
                spellCheck={false}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="field" style={{ flex: '0 1 150px', marginBottom: 0 }}>
              <div className="field-label"><span>Role</span></div>
              <select value={newRole} onChange={(e) => setNewRole(e.target.value as typeof newRole)}>
                <option value="org_admin">Administrator</option>
                <option value="org_manager">Manager</option>
                <option value="member">Member</option>
              </select>
            </div>
            <button
              className="btn btn-primary"
              style={{ height: 38 }}
              onClick={() => void createOrgUser()}
              disabled={creatingUser || !newUsername.trim()}
            >
              <IconPlus width={14} height={14} />
              {creatingUser ? 'Creating…' : 'Create user under this org'}
            </button>
          </div>
        </div>
      )}

      {data && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Role</th>
              <th>Projects</th>
              <th>Storage used</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.id}>
                <td style={{ fontWeight: 600 }}>
                  {m.username}
                  {me?.id === m.id && <span className="badge info" style={{ marginLeft: 8 }}>you</span>}
                </td>
                <td>
                  <select
                    value={m.role}
                    onChange={(e) => void changeMemberRole(m, e.target.value)}
                    title="Change this member's role"
                  >
                    <option value="org_admin">Administrator (org)</option>
                    <option value="org_manager">Manager (org)</option>
                    <option value="member">Member (org)</option>
                  </select>
                </td>
                <td className="mono">{m.projectCount}</td>
                <td className="mono">{fmtSize(m.usedBytes)}</td>
                <td>
                  {m.disabled
                    ? <span className="badge err">disabled</span>
                    : <span className="badge ok">active</span>}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn btn-sm" onClick={() => void toggleMember(m)}>
                    {m.disabled ? 'Enable' : 'Disable'}
                  </button>{' '}
                  {confirmDelete === m.id ? (
                    <button className="btn btn-sm btn-danger" onClick={() => void deleteMember(m)}>
                      Confirm delete
                    </button>
                  ) : (
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => setConfirmDelete(m.id)}
                      title="Delete member and their projects"
                    >
                      <IconTrash width={13} height={13} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {data && data.members.length === 0 && (
        <div className="admin-empty">No member accounts in this organization yet.</div>
      )}
    </div>
  );
}
